import { useTranslation } from 'react-i18next';
import type { IEquipmentMaintenanceListEntry } from '@apis/equipmentMaintenance';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { Pill } from '@components/ui/Pill/Pill';
import { Menu } from '@components/ui/Menu/Menu';
import { formatFieldDay } from '@components/ui/DateField/fieldDate';
import { formatYmd } from '@utils/dateUtils';
import { daysBetween } from '../../../../SchedulerPage/lib/timeScale';
import { maintenanceDay } from '../maintenanceDay';
import styles from './MaintenanceRow.module.css';

// "Every 1 week" reads as "Every week"; Japanese has no plural to pick it, so these are their own keys.
const EVERY_ONE = { day: 'daily', week: 'weekly', month: 'monthly' } as const;

interface IMaintenanceRowProps {
  entry: IEquipmentMaintenanceListEntry & { nextDueDate: string };
  today: string;
  tz: string;
  actions?: {
    onMarkDone: () => void;
    onEdit: () => void;
    onDelete: () => void;
  };
}

/** One open deadline: how urgent it is, how often it repeats, and when it was last done.
 *  Done today, it says so for the rest of the day, with its next date beside it. */
export function MaintenanceRow({ entry, today, tz, actions }: IMaintenanceRowProps) {
  const { t } = useTranslation();
  const days = daysBetween(today, entry.nextDueDate);
  const doneToday = entry.lastDoneAt !== null && formatYmd(entry.lastDoneAt, tz) === today;
  const urgency = doneToday ? 'done' : days < 0 ? 'overdue' : days === 0 ? 'today' : 'upcoming';
  const urgencyLabel = urgency === 'upcoming' && days === 1 ? 'tomorrow' : urgency;
  const { recurrence } = entry;
  const repeat = !recurrence
    ? t('equipment.detail.maintenance.once')
    : recurrence.every === 1
      ? t(`equipment.detail.maintenance.repeat.${EVERY_ONE[recurrence.unit]}`)
      : t(`equipment.detail.maintenance.repeat.${recurrence.unit}`, { count: recurrence.every });
  const detail = doneToday
    ? t('equipment.detail.maintenance.nextDue', { date: maintenanceDay(entry.nextDueDate, tz, today) })
    : entry.lastDoneAt
      ? t('equipment.detail.maintenance.lastDone', {
        date: maintenanceDay(entry.lastDoneAt, tz, today),
        name: entry.lastDoneByName,
      })
      : t('equipment.detail.maintenance.notDoneYet');

  return (
    <li className={styles.row}>
      <span className={styles.title}>{entry.title}</span>
      <Pill className={styles.chip} data-urgency={urgency}>
        {t(`equipment.detail.maintenance.urgency.${urgencyLabel}`, { count: Math.abs(days) })}
      </Pill>
      {actions && (
        <Menu.Root>
          <Menu.Trigger render={<KebabButton aria-label={t('equipment.detail.maintenance.rowMenu', { title: entry.title })} />} />
          <Menu.Portal>
            <Menu.Positioner side="bottom" align="end" sideOffset={4}>
              <Menu.Popup>
                {/* Done means it happened: open from its day, not before. */}
                <Menu.Item disabled={days > 0} onClick={actions.onMarkDone}>
                  {days > 0
                    ? t('equipment.detail.maintenance.markDoneFrom', { date: maintenanceDay(entry.nextDueDate, tz, today) })
                    : t('equipment.detail.maintenance.markDone', { date: formatFieldDay(today) })}
                </Menu.Item>
                <Menu.Item onClick={actions.onEdit}>{t('equipment.detail.maintenance.edit')}</Menu.Item>
                <Menu.Item onClick={actions.onDelete}>{t('common.delete')}</Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      )}
      <span className={styles.meta}>{repeat} · {detail}</span>
    </li>
  );
}
